import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { approvePruneFlag, dismissPruneFlag, fetchPruningQueue } from '@src/api';

import PruningPreviewDialog from './PruningPreviewDialog';

// Loop #5 review surface: ads the daily creative-pruning loop has flagged as
// statistically underperforming their ad-set siblings. A human approves (pauses
// on Facebook via the same endpoint as the manual button) or dismisses.
const SEVERITY_COLOR = { critical: 'error', high: 'error', medium: 'warning', low: 'default' };

const pct = (v) => `${(Number(v || 0) * 100).toFixed(1)}%`;

export default function PruningQueue({ clientIds = [] }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState({}); // uid -> 'approving' | 'dismissing'
  const [notice, setNotice] = React.useState(null);
  const [activeFlag, setActiveFlag] = React.useState(null); // flag open in the preview dialog

  const clientKey = clientIds.join(',');

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetchPruningQueue({});
      const data = Array.isArray(resp?.data) ? resp.data : [];
      const ids = clientKey ? clientKey.split(',') : [];
      setRows(ids.length ? data.filter((r) => ids.includes(String(r.client_id))) : data);
    } catch (err) {
      console.error('Failed to load pruning queue:', err);
      setError('Unable to load the pruning queue.');
    } finally {
      setLoading(false);
    }
  }, [clientKey]);

  React.useEffect(() => {
    load();
  }, [load]);

  const act = async (uid, kind) => {
    setBusy((b) => ({ ...b, [uid]: kind }));
    try {
      if (kind === 'approving') await approvePruneFlag({ uid });
      else await dismissPruneFlag({ uid });
      setRows((rs) => rs.filter((r) => r.uid !== uid));
      setActiveFlag((f) => (f && f.uid === uid ? null : f)); // close dialog if acting on it
      setNotice(kind === 'approving' ? 'Ad paused on Facebook.' : 'Flag dismissed.');
    } catch (err) {
      console.error('Pruning action failed:', err);
      setNotice('Action failed — please retry.');
    } finally {
      setBusy((b) => {
        const next = { ...b };
        delete next[uid];
        return next;
      });
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  }
  if (!rows.length) {
    return (
      <Box sx={{ p: 5, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>No ads flagged for pruning</Typography>
        <Typography variant="body2" color="text.secondary">
          The daily loop hasn’t found any ads statistically underperforming their ad-set siblings.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {notice ? (
        <Alert severity="info" onClose={() => setNotice(null)} sx={{ m: 1 }}>{notice}</Alert>
      ) : null}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Ad</TableCell>
            <TableCell>Why it’s flagged</TableCell>
            <TableCell align="right">
              <Tooltip title="Posterior probability the ad's true conversion rate is worse than its ad-set siblings" arrow>
                <span>Confidence</span>
              </Tooltip>
            </TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => {
            const m = r.metrics || {};
            const b = busy[r.uid];
            return (
              <TableRow key={r.uid} hover>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip size="small" label={r.severity} color={SEVERITY_COLOR[r.severity] || 'default'} />
                    {r.would_pause ? (
                      <Tooltip title="The loop's top auto-pause pick for this ad set this run" arrow>
                        <Chip size="small" color="error" variant="outlined" label="Recommended" />
                      </Tooltip>
                    ) : null}
                    <Box>
                      <Typography
                        variant="body2"
                        onClick={() => setActiveFlag(r)}
                        sx={{ fontWeight: 700, cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {r.ad_name || r.ad_id}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[r.client_name, r.ad_set_name || 'ad set', r.vertical].filter(Boolean).join(' · ')}
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {m.stage ? `${m.stage} ` : ''}rate {pct(m.ad_rate)} vs peers {pct(m.peer_rate)}
                    {' · '}cost ×{Number(m.cost_ratio || 0).toFixed(1)}
                    {' · '}creative −{Math.round(m.creative_gap || 0)}
                  </Typography>
                  {r.ai_hypothesis ? (
                    <Typography variant="caption" color="text.secondary">🧠 {r.ai_hypothesis}</Typography>
                  ) : null}
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {(Number(m.p_worse || 0) * 100).toFixed(0)}%
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" variant="outlined" disabled={!!b} onClick={() => setActiveFlag(r)}>
                      Preview
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      variant="contained"
                      disabled={!!b}
                      onClick={() => act(r.uid, 'approving')}
                    >
                      {b === 'approving' ? 'Pausing…' : 'Pause'}
                    </Button>
                    <Button size="small" variant="text" disabled={!!b} onClick={() => act(r.uid, 'dismissing')}>
                      {b === 'dismissing' ? '…' : 'Dismiss'}
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <PruningPreviewDialog
        flag={activeFlag}
        busy={activeFlag ? busy[activeFlag.uid] : null}
        onApprove={() => activeFlag && act(activeFlag.uid, 'approving')}
        onDismiss={() => activeFlag && act(activeFlag.uid, 'dismissing')}
        onClose={() => setActiveFlag(null)}
      />
    </Box>
  );
}
